import axios from 'axios';
import { api } from './client';

// Maps API - separate from main API, no auth required
// Hidden path prefix for security through obscurity
const MAPS_API_URL = import.meta.env.VITE_MAPS_API_URL || 'https://maps.doctor-server.com';
const MAPS_API_PREFIX = '/m8546ac86';

const mapsApi = axios.create({
  baseURL: `${MAPS_API_URL}${MAPS_API_PREFIX}`,
  timeout: 30000,
});

// Raw fetch without URL encoding (needed for polyline)
async function fetchRaw<T>(path: string): Promise<T> {
  const url = `${MAPS_API_URL}${MAPS_API_PREFIX}${path}`;
  const response = await fetch(url, { signal: AbortSignal.timeout(30000) });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
}

// Types
export interface LatLng {
  lat: number;
  lon: number;
}

export interface GeocodingResult {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [lon, lat]
  };
  properties: {
    osm_id?: number;
    osm_type?: string;
    place_id?: number;
    display_name?: string;
    name?: string;
    // Legacy fields (Photon format)
    street?: string;
    housenumber?: string;
    city?: string;
    state?: string;
    country?: string;
    postcode?: string;
    // Nominatim nested address
    address?: {
      house_number?: string;
      road?: string;
      city?: string;
      town?: string;
      state?: string;
      country?: string;
      postcode?: string;
    };
  };
}

export interface GeocodingResponse {
  type: 'FeatureCollection';
  features: GeocodingResult[];
}

export interface RouteSummary {
  distanceKm: number;
  timeMinutes: number;
}

export interface RouteStep {
  instruction: string;
  distance: number;
  time: number;
  type: number;
}

export interface RouteLeg {
  summary: {
    length: number; // km
    time: number; // seconds
  };
  shape: string; // polyline encoded
  maneuvers: RouteStep[];
}

// Unified route response (converted from OSRM format)
export interface RouteResponse {
  trip: {
    summary: {
      length: number; // km
      time: number; // seconds
    };
    legs: RouteLeg[];
    locations: LatLng[];
  };
}

// OSRM native response types
interface OSRMRouteResponse {
  code: string;
  routes: Array<{
    geometry: string; // polyline
    legs: Array<{
      distance: number; // meters
      duration: number; // seconds
      steps: Array<{
        distance: number;
        duration: number;
        name: string;
        maneuver: { type: string; modifier?: string };
      }>;
    }>;
    distance: number; // total meters
    duration: number; // total seconds
  }>;
  waypoints: Array<{
    location: [number, number]; // [lon, lat]
    name: string;
  }>;
}

interface OSRMTableResponse {
  code: string;
  durations: number[][]; // seconds, null if no route
  distances?: number[][]; // meters, optional
  sources: Array<{ location: [number, number] }>;
  destinations: Array<{ location: [number, number] }>;
}

interface OSRMTripResponse {
  code: string;
  trips: Array<{
    geometry: string;
    legs: Array<{
      distance: number;
      duration: number;
    }>;
    distance: number;
    duration: number;
  }>;
  waypoints: Array<{
    waypoint_index: number;
    trips_index: number;
    location: [number, number];
    name: string;
  }>;
}

export type CostingModel = 'auto' | 'bicycle' | 'pedestrian';

// Map costing to OSRM profile
function costingToProfile(costing: CostingModel): string {
  switch (costing) {
    case 'pedestrian':
      return 'foot';
    case 'bicycle':
      return 'bicycle';
    case 'auto':
    default:
      return 'driving';
  }
}

// Encode polyline with precision 6 (polyline6 format for OSRM)
// Based on Google's polyline algorithm
function encodePolyline6(points: LatLng[]): string {
  const precision = 6;
  let encoded = '';
  let prevLat = 0;
  let prevLon = 0;

  for (const point of points) {
    const lat = Math.round(point.lat * Math.pow(10, precision));
    const lon = Math.round(point.lon * Math.pow(10, precision));

    encoded += encodeSignedNumber(lat - prevLat);
    encoded += encodeSignedNumber(lon - prevLon);

    prevLat = lat;
    prevLon = lon;
  }

  return encoded;
}

function encodeSignedNumber(num: number): string {
  let sgn_num = num << 1;
  if (num < 0) {
    sgn_num = ~sgn_num;
  }

  let encoded = '';
  while (sgn_num >= 0x20) {
    encoded += String.fromCharCode((0x20 | (sgn_num & 0x1f)) + 63);
    sgn_num >>>= 5; // unsigned right shift
  }
  encoded += String.fromCharCode(sgn_num + 63);

  return encoded;
}

// Format coordinates for OSRM
// Uses polyline6 encoding for large coordinate sets to avoid URL length limits
function formatCoords(locations: LatLng[]): string {
  // For small sets, use simple format (more readable in logs)
  if (locations.length <= 25) {
    return locations.map((l) => `${l.lon},${l.lat}`).join(';');
  }
  // For large sets, use polyline6 encoding (precision 6)
  // URL-encode the polyline to escape special chars like }, |, ], @ (same as backend)
  const polyline = encodeURIComponent(encodePolyline6(locations));
  return `polyline6(${polyline})`;
}

// ==================== GEOCODING (Nominatim - unchanged) ====================

/**
 * Search for address by text query
 * Uses Nominatim geocoding service
 */
export async function searchAddress(
  query: string,
  options?: { lat?: number; lon?: number; limit?: number }
): Promise<GeocodingResponse> {
  const params = new URLSearchParams({
    q: query,
    format: 'geojson',
    addressdetails: '1',
    countrycodes: 'by', // Belarus only
  });
  if (options?.limit !== undefined) params.append('limit', String(options.limit));

  // Use viewbox for location bias if coordinates provided
  if (options?.lat !== undefined && options?.lon !== undefined) {
    const delta = 0.5;
    params.append(
      'viewbox',
      `${options.lon - delta},${options.lat - delta},${options.lon + delta},${options.lat + delta}`
    );
    params.append('bounded', '0');
  }

  const response = await mapsApi.get<GeocodingResponse>(`/search?${params}`);
  return response.data;
}

/**
 * Reverse geocoding - get address by coordinates
 * Uses Nominatim
 */
export async function reverseGeocode(lat: number, lon: number): Promise<GeocodingResponse> {
  const response = await mapsApi.get<GeocodingResponse>('/reverse', {
    params: {
      lat,
      lon,
      format: 'geojson',
      addressdetails: '1',
    },
  });
  return response.data;
}

// ==================== ROUTING (OSRM) ====================

/**
 * Calculate route between two or more points
 * Uses OSRM routing service
 */
export async function getRoute(
  from: LatLng,
  to: LatLng,
  costing: CostingModel = 'auto'
): Promise<RouteResponse> {
  const profile = costingToProfile(costing);
  const coords = formatCoords([from, to]);

  const response = await mapsApi.get<OSRMRouteResponse>(
    `/route/v1/${profile}/${coords}?overview=full&geometries=polyline&steps=true`
  );

  const osrm = response.data;
  if (osrm.code !== 'Ok' || !osrm.routes[0]) {
    throw new Error(`OSRM error: ${osrm.code}`);
  }

  const route = osrm.routes[0];

  // Convert to unified format
  return {
    trip: {
      summary: {
        length: route.distance / 1000, // meters to km
        time: route.duration,
      },
      legs: route.legs.map((leg) => ({
        summary: {
          length: leg.distance / 1000,
          time: leg.duration,
        },
        shape: route.geometry,
        maneuvers: leg.steps.map((step) => ({
          instruction: step.name || step.maneuver.type,
          distance: step.distance,
          time: step.duration,
          type: 0,
        })),
      })),
      locations: osrm.waypoints.map((wp) => ({
        lat: wp.location[1],
        lon: wp.location[0],
      })),
    },
  };
}

/**
 * Calculate route through multiple points (keeps order)
 */
export async function getMultiPointRoute(
  locations: LatLng[],
  costing: CostingModel = 'auto'
): Promise<RouteResponse> {
  if (locations.length < 2) {
    throw new Error('Need at least 2 locations');
  }

  const profile = costingToProfile(costing);
  const coords = formatCoords(locations);

  // Use raw fetch for polyline to avoid URL encoding
  const osrm = await fetchRaw<OSRMRouteResponse>(
    `/route/v1/${profile}/${coords}?overview=full&geometries=polyline&steps=true`
  );
  if (osrm.code !== 'Ok' || !osrm.routes[0]) {
    throw new Error(`OSRM error: ${osrm.code}`);
  }

  const route = osrm.routes[0];

  return {
    trip: {
      summary: {
        length: route.distance / 1000,
        time: route.duration,
      },
      legs: route.legs.map((leg) => ({
        summary: {
          length: leg.distance / 1000,
          time: leg.duration,
        },
        shape: route.geometry, // Full geometry, could split per leg if needed
        maneuvers:
          leg.steps?.map((step) => ({
            instruction: step.name || step.maneuver.type,
            distance: step.distance,
            time: step.duration,
            type: 0,
          })) || [],
      })),
      locations: osrm.waypoints.map((wp) => ({
        lat: wp.location[1],
        lon: wp.location[0],
      })),
    },
  };
}

/**
 * Get route summary (distance and time only)
 */
export async function getRouteSummary(
  from: LatLng,
  to: LatLng,
  costing: CostingModel = 'auto'
): Promise<RouteSummary> {
  const route = await getRoute(from, to, costing);
  return {
    distanceKm: route.trip.summary.length,
    timeMinutes: Math.round(route.trip.summary.time / 60),
  };
}

// ==================== DISTANCE MATRIX (OSRM Table) ====================

/**
 * Distance matrix response (unified format)
 */
export interface DistanceMatrixResponse {
  sources_to_targets: Array<Array<{ distance: number; time: number } | null>>;
}

/**
 * Get distance/time matrix between points
 * Uses OSRM table API
 */
export async function getDistanceMatrix(
  locations: LatLng[],
  costing: CostingModel = 'auto'
): Promise<DistanceMatrixResponse> {
  const profile = costingToProfile(costing);
  const coords = formatCoords(locations);

  // Use raw fetch for polyline to avoid URL encoding
  const osrm = await fetchRaw<OSRMTableResponse>(
    `/table/v1/${profile}/${coords}?annotations=duration,distance`
  );
  if (osrm.code !== 'Ok') {
    throw new Error(`OSRM table error: ${osrm.code}`);
  }

  // Convert to unified format
  const sources_to_targets: Array<Array<{ distance: number; time: number } | null>> = [];

  for (let i = 0; i < osrm.durations.length; i++) {
    const row: Array<{ distance: number; time: number } | null> = [];
    for (let j = 0; j < osrm.durations[i].length; j++) {
      const duration = osrm.durations[i][j];
      const distance = osrm.distances?.[i]?.[j];

      if (duration === null || duration === undefined) {
        row.push(null);
      } else {
        row.push({
          time: duration,
          distance: distance !== undefined ? distance / 1000 : 0, // meters to km
        });
      }
    }
    sources_to_targets.push(row);
  }

  return { sources_to_targets };
}

// ==================== TSP OPTIMIZATION (OSRM Trip) ====================

export interface TripResponse {
  waypoints: Array<{
    waypoint_index: number; // Original index in input
    location: LatLng;
  }>;
  route: RouteResponse;
}

/**
 * Calculate optimized route visiting multiple points (TSP)
 * Uses OSRM trip API
 *
 * @param locations - Array of lat/lon points
 * @param options.fixStart - If true, first point is fixed as start
 * @param options.roundtrip - If true, return to start (default false)
 */
export async function getTripRoute(
  locations: LatLng[],
  costing: CostingModel = 'auto',
  options?: { fixStart?: boolean; roundtrip?: boolean }
): Promise<TripResponse> {
  if (locations.length < 2) {
    throw new Error('Need at least 2 locations');
  }

  const profile = costingToProfile(costing);
  const coords = formatCoords(locations);

  const params = new URLSearchParams({
    geometries: 'polyline',
    overview: 'full',
    steps: 'true',
  });

  // source=first means first point is fixed as start
  if (options?.fixStart) {
    params.set('source', 'first');
  }

  // roundtrip=false means don't return to start
  params.set('roundtrip', options?.roundtrip ? 'true' : 'false');

  // Use raw fetch for polyline to avoid URL encoding
  const osrm = await fetchRaw<OSRMTripResponse>(
    `/trip/v1/${profile}/${coords}?${params}`
  );
  if (osrm.code !== 'Ok' || !osrm.trips[0]) {
    throw new Error(`OSRM trip error: ${osrm.code}`);
  }

  const trip = osrm.trips[0];

  return {
    waypoints: osrm.waypoints.map((wp) => ({
      waypoint_index: wp.waypoint_index,
      location: {
        lat: wp.location[1],
        lon: wp.location[0],
      },
    })),
    route: {
      trip: {
        summary: {
          length: trip.distance / 1000,
          time: trip.duration,
        },
        legs: trip.legs.map((leg) => ({
          summary: {
            length: leg.distance / 1000,
            time: leg.duration,
          },
          shape: trip.geometry,
          maneuvers: [],
        })),
        locations: osrm.waypoints.map((wp) => ({
          lat: wp.location[1],
          lon: wp.location[0],
        })),
      },
    },
  };
}

// ==================== UTILITIES ====================

/**
 * Get maps API health status
 */
export async function getMapsHealth(): Promise<{
  status: string;
  services: string[];
}> {
  const response = await mapsApi.get('/health');
  return response.data;
}

/**
 * Get map style URL for MapLibre
 */
export function getMapStyleUrl(): string {
  return `${MAPS_API_URL}${MAPS_API_PREFIX}/style.json`;
}

/**
 * Get PMTiles URL for MapLibre (with pmtiles:// protocol)
 */
export function getPmtilesUrl(): string {
  return `pmtiles://${MAPS_API_URL.replace('https://', '').replace('http://', '')}/tiles/belarus.pmtiles`;
}

// ==================== CACHED DISTANCES (via main API) ====================

/**
 * Cached distance matrix between clients
 * Keys are client IDs, values are distances to other clients
 */
export interface CachedDistanceMatrix {
  [fromClientId: number]: {
    [toClientId: number]: { distanceM: number; durationS: number } | null;
  };
}

/**
 * Get cached distances between clients from PostgreSQL cache
 * Returns MIN distance between all parking locations of each client pair
 *
 * @param clientIds - Array of client IDs to get distances for
 * @returns Distance matrix with distances in meters and durations in seconds
 */
export async function getCachedDistances(clientIds: number[]): Promise<CachedDistanceMatrix> {
  if (clientIds.length < 2) {
    return {};
  }

  const response = await api.get<{ matrix: CachedDistanceMatrix }>('/geo/distances', {
    params: { clientIds: clientIds.join(',') },
  });

  return response.data.matrix;
}

// ==================== ROUTE OPTIMIZATION (via main API) ====================

export type OptimizeRouteMode = 'fromLast' | 'fromMe' | 'shortest';

export interface OptimizeRouteRequest {
  clientIds: number[];
  mode: OptimizeRouteMode;
  startClientId?: number;      // For fromLast mode
  startPosition?: LatLng;      // For fromMe mode
}

export interface OptimizeRouteResult {
  /** Ordered client IDs to visit */
  order: number[];
  /** Total route distance in meters */
  totalDistanceM: number;
  /** Total route duration in seconds */
  totalDurationS: number;
  /** Starting client ID (for fromMe mode - nearest to GPS) */
  startClientId?: number;
  /** Clients with no cached distances (at the end of route) */
  unreachable: number[];
}

/**
 * Optimize visit order using cached distances (Nearest Neighbor on backend)
 * Much faster than downloading full matrix and calculating on frontend
 *
 * Modes:
 * - fromLast: Start from last visited client (startClientId)
 * - fromMe: Start from GPS position (startPosition) - finds nearest client
 * - shortest: Try all start points, pick best route
 */
export async function optimizeRoute(request: OptimizeRouteRequest): Promise<OptimizeRouteResult> {
  if (request.clientIds.length <= 1) {
    return {
      order: request.clientIds,
      totalDistanceM: 0,
      totalDurationS: 0,
      unreachable: [],
    };
  }

  const response = await api.post<OptimizeRouteResult>('/geo/optimize-route', request);
  return response.data;
}

// ==================== PRIORITY-BASED ROUTE OPTIMIZATION ====================

export interface OptimizeRoutePriorityRequest {
  clientIds: number[];
  priorities: number[]; // Priority value for each client (lower = higher priority)
  mode: OptimizeRouteMode;
  startClientId?: number;
  startPosition?: LatLng;
}

/**
 * Optimize route with priority groups (backend)
 *
 * Groups clients by priority, then optimizes route within each group.
 * Transitions between groups are optimized (nearest entry point).
 *
 * Example:
 * - urgent clients: [A, B, C] → optimized as [B, A, C]
 * - deadline clients: [D, E] → optimized starting from nearest to C
 * - regular clients: [F, G] → optimized starting from nearest to E
 */
export async function optimizeRouteWithPriorities(
  request: OptimizeRoutePriorityRequest
): Promise<OptimizeRouteResult> {
  if (request.clientIds.length <= 1) {
    return {
      order: request.clientIds,
      totalDistanceM: 0,
      totalDurationS: 0,
      unreachable: [],
    };
  }

  const response = await api.post<OptimizeRouteResult>(
    '/geo/optimize-route-priority',
    request
  );
  return response.data;
}
